"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { useT, type TFn } from "@/lib/i18n";
import { displayName } from "@/lib/display-name";
import type { SceneStep, StepDetail } from "@/lib/replay-scene";
import LiveScene from "@/app/[locale]/live/[steamId]/LiveScene";
import { useLiveCatalogs } from "@/app/[locale]/live/live-shared";
import { KIND_LABEL, cardName, encounterName, monsterName, moveName, potionName, relicName, type Catalog } from "./FloorPanel";
const SPEEDS = [0.5, 1, 2, 4];
const BASE_MS = 1100;
const DECISION_LABEL: Record<string, string> = {
  card_reward: "Card reward",
  relic_reward: "Relic",
  treasure: "Treasure",
  rest: "Rest site",
  map_node: "Map",
  event: "Event",
  deck_select_remove: "Card removal"
};
function who(id: string | undefined, cat: Catalog, t: TFn): string {
  if (!id || id === "player") return t("You");
  return monsterName(id, cat);
}
function caption(step: SceneStep, cat: Catalog, t: TFn): string {
  const c = step.caption;
  const id = c.id ?? "";
  switch (c.k) {
    case "room":
      if (c.label === "combat") return encounterName(id, cat) || t("Combat");
      if (c.label === "event" && id) return cat.events[id]?.name || displayName(`EVENT.${id}`);
      return t(KIND_LABEL[c.label ?? ""] ?? "Floor");
    case "combat_start":
      return t("Fight start");
    case "turn":
      return c.side === "enemy" ? `${t("Enemy")} · ${t("Turn")} ${c.n ?? ""}`.trim() : `${t("Turn")} ${c.n ?? ""}`.trim();
    case "end_turn":
      return t("End of turn");
    case "play":
      {
        const name = cardName(id, cat) + (c.up ? "+" : "");
        return c.target ? `${t("Played")} ${name} → ${monsterName(c.target, cat)}` : `${t("Played")} ${name}`;
      }
    case "move":
      return t("{monster} used {move}", {
        monster: monsterName(c.src ?? "", cat),
        move: moveName(c.src ?? "", id, cat)
      });
    case "potion":
      return c.target ? `${t("Used {potion}", {
        potion: potionName(id, cat)
      })} → ${monsterName(c.target, cat)}` : t("Used {potion}", {
        potion: potionName(id, cat)
      });
    case "potion_got":
      return `${t("Taken")}: ${potionName(id, cat)}`;
    case "potion_dropped":
      return `${t("Removed")}: ${potionName(id, cat)}`;
    case "relic":
      return `${t("Taken")}: ${relicName(id, cat)}`;
    case "relic_lost":
      return `${t("Removed")}: ${relicName(id, cat)}`;
    case "acquire":
      return t("Added {n} × {card} to your deck", {
        n: 1,
        card: cardName(id, cat)
      });
    case "remove":
      return `${t("Removed")}: ${cardName(id, cat)}`;
    case "upgrade":
      return t("Upgraded {card}", {
        card: cardName(id, cat)
      });
    case "transform":
      return t("{from} became {to}", {
        from: cardName(c.src ?? "", cat),
        to: cardName(id, cat)
      });
    case "combat_end":
      return c.label === "victory" ? t("Victory") : t("Combat");
    case "decision":
      if (c.label === "event" && id) return cat.events[id]?.name || displayName(`EVENT.${id}`);
      return t(DECISION_LABEL[c.label ?? ""] ?? "Choose cards");
    case "outcome":
      if (c.src === "skip") return t("Skipped");
      if (c.label === "map_node") return t("Map");
      return c.option ? `${t("Chose")}: ${c.option}` : t("Chose");
    case "resolve":
      if (c.label === "GoldReward" && c.n !== undefined) return `${t("Taken")}: ${c.n} ${t("gold")}`;
      if (c.label === "PotionReward" && id) return `${t("Taken")}: ${potionName(id, cat)}`;
      if (c.label === "RelicReward" && id) return `${t("Taken")}: ${relicName(id, cat)}`;
      return t("Reward screen");
    case "shop":
      return t("Shop");
    case "buy":
      {
        const name = c.label === "card" ? cardName(id, cat) : c.label === "relic" ? relicName(id, cat) : c.label === "potion" ? potionName(id, cat) : t("Card removal");
        return `${t("Bought")}: ${name}${c.n !== undefined ? ` (${c.n} ${t("gold")})` : ""}`;
      }
    case "rest":
      return c.option === "heal" ? t("Rested at a campfire") : c.option === "smith" ? t("{who} smithed at the campfire", {
        who: t("You")
      }) : t("{who} chose {option} at the campfire", {
        who: t("You"),
        option: displayName(c.option ?? "")
      });
    case "resume":
      return t("Reloaded from a save");
    case "heal":
      {
        const healed = step.details.find(d => d.k === "heal");
        return t("Healed {n} HP", {
          n: healed?.n ?? 0
        });
      }
    default:
      return displayName(c.k);
  }
}
function detailText(d: StepDetail, cat: Catalog, t: TFn): string {
  switch (d.k) {
    case "hit":
      if (d.dst === "player") return t("{monster} hit you for {n}", {
        monster: who(d.src, cat, t),
        n: d.n ?? 0
      });
      return t("{target} took {n} damage", {
        target: monsterName(d.dst ?? "", cat),
        n: d.n ?? 0
      });
    case "block":
      return d.dst === "player" ? `${t("Block")} +${d.n}` : t("{monster} gained {n} Block", {
        monster: monsterName(d.dst ?? "", cat),
        n: d.n ?? 0
      });
    case "power":
      {
        const power = `${displayName((d.id ?? "").replace(/_POWER$/, ""))}${d.n !== undefined ? ` ${d.n}` : ""}`;
        return d.dst === "player" ? t("{power} applied to you", {
          power
        }) : t("Applied {power} to {target}", {
          power,
          target: monsterName(d.dst ?? "", cat)
        });
      }
    case "death":
      return d.dst === "player" ? t("You died here") : t("killed {who}", {
        who: monsterName(d.dst ?? "", cat)
      });
    case "draw":
      return `${t("Drew")} ${(d.ids ?? []).map(x => cardName(x, cat)).join(", ")}`;
    case "created":
      return t("Created {card}", {
        card: (d.ids ?? []).map(x => cardName(x, cat)).join(", ")
      });
    case "heal":
      return t("Healed {n} HP", {
        n: d.n ?? 0
      });
    case "exhaust":
      return `${t("Exhaust")}: ${cardName(d.id ?? "", cat)}`;
    case "discard":
      return `${t("Discard")}: ${cardName(d.id ?? "", cat)}`;
  }
}
export default function ReplayScene(t0) {
  const $ = _c(100);
  const {
    steps,
    index,
    onIndex,
    cat,
    bp
  } = t0;
  const t = useT();
  const live = useLiveCatalogs();
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const step = steps[index];
  let t1;
  let t2;
  if ($[0] !== index || $[1] !== onIndex || $[2] !== playing || $[3] !== speed || $[4] !== steps.length) {
    t1 = () => {
      if (!playing) {
        return;
      }
      const id = window.setTimeout(() => {
        if (index + 1 < steps.length) {
          onIndex(index + 1);
        } else {
          setPlaying(false);
        }
      }, BASE_MS / speed);
      return () => window.clearTimeout(id);
    };
    t2 = [playing, index, speed, steps.length, onIndex];
    $[0] = index;
    $[1] = onIndex;
    $[2] = playing;
    $[3] = speed;
    $[4] = steps.length;
    $[5] = t1;
    $[6] = t2;
  } else {
    t1 = $[5];
    t2 = $[6];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[7] !== index || $[8] !== onIndex || $[9] !== steps.length) {
    t3 = () => {
      const onKey = e => {
        if (e.altKey || e.ctrlKey || e.metaKey || e.defaultPrevented) {
          return;
        }
        const target = e.target as HTMLElement | null;
        if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.tagName === "SELECT" || target.tagName === "BUTTON" || target.isContentEditable)) {
          return;
        }
        if (e.key === "ArrowRight" && index + 1 < steps.length) {
          e.preventDefault();
          onIndex(index + 1);
        } else {
          if (e.key === "ArrowLeft" && index > 0) {
            e.preventDefault();
            onIndex(index - 1);
          } else {
            if (e.key === " ") {
              e.preventDefault();
              setPlaying(_temp);
            }
          }
        }
      };
      window.addEventListener("keydown", onKey);
      return () => window.removeEventListener("keydown", onKey);
    };
    t4 = [index, steps.length, onIndex];
    $[7] = index;
    $[8] = onIndex;
    $[9] = steps.length;
    $[10] = t3;
    $[11] = t4;
  } else {
    t3 = $[10];
    t4 = $[11];
  }
  useEffect(t3, t4);
  if (!step) {
    return null;
  }
  let first = index;
  while (first > 0 && steps[first - 1].floor === step.floor) {
    first = first - 1;
  }
  let last = index;
  while (last + 1 < steps.length && steps[last + 1].floor === step.floor) {
    last = last + 1;
  }
  let t5;
  if ($[12] !== bp || $[13] !== cat.encounters || $[14] !== cat.monsters || $[15] !== live || $[16] !== step.p) {
    t5 = <LiveScene p={step.p} cat={live} monsters={cat.monsters} encounters={cat.encounters} bp={bp} height="min-h-[70vh]" />;
    $[12] = bp;
    $[13] = cat.encounters;
    $[14] = cat.monsters;
    $[15] = live;
    $[16] = step.p;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== cat || $[19] !== step || $[20] !== t) {
    t6 = caption(step, cat, t);
    $[18] = cat;
    $[19] = step;
    $[20] = t;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== t6) {
    t7 = <p className="text-sm font-semibold text-[var(--text-primary)]">{t6}</p>;
    $[22] = t6;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  const t8 = index - first + 1;
  const t9 = last - first + 1;
  let t10;
  if ($[24] !== t || $[25] !== t8 || $[26] !== t9) {
    t10 = t("Step {n} of {total}", {
      n: t8,
      total: t9
    });
    $[24] = t;
    $[25] = t8;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== t10) {
    t11 = <span className="text-xs tabular-nums text-[var(--text-muted)]">{t10}</span>;
    $[28] = t10;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== t11 || $[31] !== t7) {
    t12 = <div className="flex flex-wrap items-baseline justify-between gap-2">{t7}{t11}</div>;
    $[30] = t11;
    $[31] = t7;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== cat || $[34] !== step.details || $[35] !== t) {
    t13 = step.details.length > 0 && <ul className="mt-1 space-y-0.5 text-xs text-[var(--text-secondary)]">{step.details.map((d, i) => <li key={i}>{detailText(d, cat, t)}</li>)}</ul>;
    $[33] = cat;
    $[34] = step.details;
    $[35] = t;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== cat || $[38] !== step.created || $[39] !== t) {
    t14 = step.created.length > 0 && <p className="mt-1 text-xs text-[var(--text-muted)]">{t("Created, pile not recorded")}:{" "}{step.created.map(x => cardName(x.replace(/\+$/, ""), cat)).join(", ")}</p>;
    $[37] = cat;
    $[38] = step.created;
    $[39] = t;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== t12 || $[42] !== t13 || $[43] !== t14) {
    t15 = <div className="rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-primary)] px-3 py-2">{t12}{t13}{t14}</div>;
    $[41] = t12;
    $[42] = t13;
    $[43] = t14;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] !== index || $[46] !== onIndex) {
    t16 = () => onIndex(index - 1);
    $[45] = index;
    $[46] = onIndex;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  const t17 = index === 0;
  let t18;
  if ($[48] !== t) {
    t18 = t("Previous step");
    $[48] = t;
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  let t19;
  if ($[50] !== t16 || $[51] !== t17 || $[52] !== t18) {
    t19 = <button type="button" className="rounded-md border border-[var(--border-subtle)] px-2.5 py-1 text-sm font-semibold text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)] disabled:opacity-40" onClick={t16} disabled={t17} aria-label={t18}>◀</button>;
    $[50] = t16;
    $[51] = t17;
    $[52] = t18;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  let t20;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = () => setPlaying(_temp2);
    $[54] = t20;
  } else {
    t20 = $[54];
  }
  let t21;
  if ($[55] !== playing || $[56] !== t) {
    t21 = playing ? `❚❚ ${t("Pause")}` : `▶ ${t("Play")}`;
    $[55] = playing;
    $[56] = t;
    $[57] = t21;
  } else {
    t21 = $[57];
  }
  let t22;
  if ($[58] !== t21) {
    t22 = <button type="button" className="rounded-md border border-[var(--border-subtle)] px-2.5 py-1 text-sm font-semibold text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)] disabled:opacity-40 min-w-[5.5rem]" onClick={t20}>{t21}</button>;
    $[58] = t21;
    $[59] = t22;
  } else {
    t22 = $[59];
  }
  let t23;
  if ($[60] !== index || $[61] !== onIndex) {
    t23 = () => onIndex(index + 1);
    $[60] = index;
    $[61] = onIndex;
    $[62] = t23;
  } else {
    t23 = $[62];
  }
  const t24 = index + 1 >= steps.length;
  let t25;
  if ($[63] !== t) {
    t25 = t("Next step");
    $[63] = t;
    $[64] = t25;
  } else {
    t25 = $[64];
  }
  let t26;
  if ($[65] !== t23 || $[66] !== t24 || $[67] !== t25) {
    t26 = <button type="button" className="rounded-md border border-[var(--border-subtle)] px-2.5 py-1 text-sm font-semibold text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)] disabled:opacity-40" onClick={t23} disabled={t24} aria-label={t25}>▶</button>;
    $[65] = t23;
    $[66] = t24;
    $[67] = t25;
    $[68] = t26;
  } else {
    t26 = $[68];
  }
  const t27 = first;
  const t28 = last;
  let t29;
  if ($[69] !== onIndex) {
    t29 = e_0 => onIndex(Number(e_0.target.value));
    $[69] = onIndex;
    $[70] = t29;
  } else {
    t29 = $[70];
  }
  const t30 = index - first + 1;
  const t31 = last - first + 1;
  let t32;
  if ($[71] !== t || $[72] !== t30 || $[73] !== t31) {
    t32 = t("Step {n} of {total}", {
      n: t30,
      total: t31
    });
    $[71] = t;
    $[72] = t30;
    $[73] = t31;
    $[74] = t32;
  } else {
    t32 = $[74];
  }
  let t33;
  if ($[75] !== first || $[76] !== index || $[77] !== last || $[78] !== t29 || $[79] !== t32) {
    t33 = <input type="range" min={t27} max={t28} value={index} onChange={t29} className="min-w-[8rem] flex-1 accent-[var(--accent-gold)]" aria-label={t32} />;
    $[75] = first;
    $[76] = index;
    $[77] = last;
    $[78] = t29;
    $[79] = t32;
    $[80] = t33;
  } else {
    t33 = $[80];
  }
  let t34;
  if ($[81] !== speed) {
    t34 = SPEEDS.map(s => <button key={s} type="button" onClick={() => setSpeed(s)} className={`rounded px-1.5 py-0.5 text-xs tabular-nums ${s === speed ? "bg-[var(--accent-gold)]/20 text-[var(--accent-gold)]" : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"}`}>{s}×</button>);
    $[81] = speed;
    $[82] = t34;
  } else {
    t34 = $[82];
  }
  let t35;
  if ($[83] !== t34) {
    t35 = <div className="flex gap-1">{t34}</div>;
    $[83] = t34;
    $[84] = t35;
  } else {
    t35 = $[84];
  }
  let t36;
  if ($[85] !== t19 || $[86] !== t22 || $[87] !== t26 || $[88] !== t33 || $[89] !== t35) {
    t36 = <div className="flex flex-wrap items-center gap-2">{t19}{t22}{t26}{t33}{t35}</div>;
    $[85] = t19;
    $[86] = t22;
    $[87] = t26;
    $[88] = t33;
    $[89] = t35;
    $[90] = t36;
  } else {
    t36 = $[90];
  }
  let t37;
  if ($[91] !== t) {
    t37 = t("\u2190 \u2192 step through the replay \xB7 Space plays");
    $[91] = t;
    $[92] = t37;
  } else {
    t37 = $[92];
  }
  let t38;
  if ($[93] !== t37) {
    t38 = <p className="text-xs text-[var(--text-muted)]">{t37}</p>;
    $[93] = t37;
    $[94] = t38;
  } else {
    t38 = $[94];
  }
  let t39;
  if ($[95] !== t15 || $[96] !== t36 || $[97] !== t38 || $[98] !== t5) {
    t39 = <div className="space-y-3">{t5}{t15}{t36}{t38}</div>;
    $[95] = t15;
    $[96] = t36;
    $[97] = t38;
    $[98] = t5;
    $[99] = t39;
  } else {
    t39 = $[99];
  }
  return t39;
}
function _temp2(v_0) {
  return !v_0;
}
function _temp(v) {
  return !v;
}